import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { Bell, CheckCircle, Warning, Sun, ListChecks, UserPlus, ClipboardText, Moon, X, Trash } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const ICONS = {
  assignment: UserPlus, review: ClipboardText, review_result: CheckCircle,
  due: Bell, overdue: Warning, morning: Sun, endofday: Moon,
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const loadCount = useCallback(async () => {
    try { const { data } = await api.get("/notifications/unread-count"); setUnread(data.count); } catch {}
  }, []);

  const loadNotes = useCallback(async () => {
    try { const { data } = await api.get("/notifications"); setNotes(data); } catch {}
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 20000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadNotes();
  };

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    setUnread(0);
    setNotes((ns) => ns.map((n) => ({ ...n, read: true })));
  };

  const markRead = async (id) => {
    await api.patch(`/notifications/${id}/read`);
    setNotes((ns) => ns.map((n) => (n.id === id ? { ...n, read: true } : n)));
    loadCount();
  };

  const deleteNote = async (id) => {
    setNotes((ns) => ns.filter((n) => n.id !== id));
    try { await api.delete(`/notifications/${id}`); } catch {}
    loadCount();
  };

  const clearAll = async () => {
    if (!window.confirm("Delete all notifications? This can't be undone.")) return;
    setNotes([]); setUnread(0);
    try { await api.delete("/notifications"); } catch {}
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell-btn" onClick={toggle} className="press relative w-9 h-9 rounded-full border-2 border-black flex items-center justify-center bg-white">
        <Bell size={16} weight="bold" />
        {unread > 0 && (
          <span data-testid="notif-unread-badge" className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-primary text-white text-[11px] font-bold border-2 border-black flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="notif-panel"
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="absolute right-0 mt-3 w-80 max-h-[70vh] overflow-y-auto bg-white border-2 border-black rounded-xl hard-shadow z-50"
          >
            <div className="sticky top-0 bg-white border-b-2 border-black px-4 py-3 flex items-center justify-between gap-2">
              <span className="font-display font-bold">Notifications</span>
              <div className="flex items-center gap-3">
                <button data-testid="notif-mark-all-btn" onClick={markAllRead} className="text-xs font-bold text-primary">Mark all read</button>
                {notes.length > 0 && <button data-testid="notif-clear-all-btn" onClick={clearAll} className="text-xs font-bold text-red-600 flex items-center gap-1"><Trash size={12} weight="bold" /> Clear all</button>}
              </div>
            </div>
            {notes.length === 0 ? (
              <div className="p-8 text-center text-sm text-black/40">No notifications yet.</div>
            ) : (
              notes.map((n) => {
                const Icon = ICONS[n.type] || Bell;
                return (
                  <div key={n.id} data-testid={`notif-${n.id}`}
                    className={`w-full px-4 py-3 border-b border-black/10 flex gap-3 items-start ${n.read ? "bg-white" : "bg-[#FEF9C3]"}`}>
                    <button onClick={() => markRead(n.id)} className="flex gap-3 items-start text-left min-w-0 flex-1">
                      <div className="w-8 h-8 shrink-0 rounded-full border-2 border-black flex items-center justify-center bg-white"><Icon size={14} weight="bold" /></div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold leading-tight">{n.title}</p>
                        <p className="text-xs text-black/60 mt-0.5">{n.body}</p>
                        <p className="text-[10px] text-black/40 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                      </div>
                    </button>
                    <button data-testid={`notif-delete-${n.id}`} onClick={() => deleteNote(n.id)} aria-label="Delete notification"
                      className="press shrink-0 w-7 h-7 rounded-full border border-black/20 flex items-center justify-center text-black/40 hover:text-red-600 hover:border-red-600">
                      <X size={13} weight="bold" />
                    </button>
                  </div>
                );
              })
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
